import {ItineraryRepositoryType} from "./interfaces/ItineraryRepositoryType.ts";
import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    HttpStatusCode,
    InternalAxiosRequestConfig
} from 'axios'
import {AuthenticationRepository} from "./AuthenticationRepository.ts";
import {firstValueFrom} from "rxjs";
import HTTPConverter from "./converters/HTTPConverter.ts";
import generalErrorConverter from "./converters/GeneralErrorConverter.ts";
import {Itinerary} from "../domain/entities/Itinerary.ts";

export class ItineraryRepository implements ItineraryRepositoryType {

    client: AxiosInstance

    get serverBaseURL(): string {
        return import.meta.env.VITE_APP_SERVER_URL
    }

    constructor(authenticationRepository: AuthenticationRepository) {
        this.client = axios.create({
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            timeout: 25000,
            validateStatus: (status: number) => status >= 200 && status < 300
        })

        // get token from async storage and set it to the header
        this.client.interceptors.request.use(
            async (
                config: InternalAxiosRequestConfig
            ): Promise<InternalAxiosRequestConfig & { retry: number }> => {
                const token = await firstValueFrom(authenticationRepository.getIDToken())
                config.headers.set('Authorization', `Bearer ${token}`)

                const configWithRetry = config as InternalAxiosRequestConfig & {
                    retry: number
                }
                return { ...config, retry: configWithRetry.retry === 0 ? 0 : 1 }
            }
        )

        // add reponse data converter
        this.client.interceptors.response.use(
            (response) => HTTPConverter.fromResponse(response),
            async (err: AxiosError) => {
                const config = err.config as AxiosRequestConfig & { retry: number }
                if (err?.response?.status === HttpStatusCode.Unauthorized && config && config.retry > 0) {
                    config.retry = 0
                    return this.client.request(config)
                }
                return generalErrorConverter.fromBackendServerError(err)
            }
        )
    }

    async getItineraries(): Promise<Itinerary[]> {
        return this.client.get(`${this.serverBaseURL}/trip-itineraries`)
    }

    async createUpdateItinerary(itinerary: Itinerary): Promise<Itinerary> {

        const formData = new FormData()
        if(itinerary.id) {
            formData.append('id', itinerary.id)
        }
        if(itinerary.name){
            formData.append('name', itinerary.name)
        }

        if(itinerary.file){
            formData.append('file', itinerary.file)
        }

        if(itinerary.countryCodes){
            formData.append('countryCodes', itinerary.countryCodes.join(','))
        }

        if(itinerary.observations){
            formData.append('observations', itinerary.observations)
        }
        if(itinerary.personalized){
            formData.append('personalized', itinerary.personalized.toString())
        }
        return this.client.post(`${this.serverBaseURL}/trip-itineraries`, formData,{
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
    }

    async deleteItinerary(itineraryId: string): Promise<void> {
        return this.client.delete(`${this.serverBaseURL}/trip-itineraries/${itineraryId}`)
    }

    async downloadItineraryPDF(itineraryId: string): Promise<any> {
        return this.client.get(`${this.serverBaseURL}/trip-itineraries/${itineraryId}/pdf`)
    }
}
